import classNames from 'classnames';
import React, { useState } from 'react';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import AlertPill from '@/components/molecules/alert-pill/alert-pill';
import Button from '@/components/molecules/button/button';
import Tag, { TagVariant } from '@/components/molecules/tag/tag';
import ExtraCardModal from '@/components/organisms/extra-card-modal/extra-card-modal';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import PriceItems from '@/components/organisms/price-items/price-items';
import TruncatedText from '@/components/organisms/truncated-text/truncated-text';
import Link from '@/components/utilities/link/link';
import { ModuleWrapperProps } from '@/components/utilities/module-wrapper/module-wrapper';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './booking-extra-teaser.module.scss';

export interface BookingExtraTeaserProps extends ModuleWrapperProps {
  /** The children to render */
  children?: React.ReactNode;
  /** The class name to apply */
  className?: string;
  /** The description */
  description?: string;
  /** The headline */
  headline?: string;
  /** The images to display */
  images?: Content.Components['ImageMediaBlockDto'][];
  /** Whether the card is fetching */
  isFetching?: boolean;
  /** Whether the selection comes from the wishlist */
  isInWishlist?: boolean;
  /** Whether the component is interactable or not */
  isInteractable?: boolean;
  /** Whether the card is loading */
  isLoading?: boolean;
  /** Callback for the add button */
  onClickButton?: () => void;
  /** Callback for the edit link */
  onClickEditLink?: () => void;
  /** The overlay description */
  overlayDescription?: string;
  /** The overlay headline */
  overlayHeadline?: string;
  /** The price items to display */
  priceItems?: Content.Components['PriceItemDto'][];
  /** Indicates whether to display the validation error */
  shouldHighlightErrors?: boolean;
  /** State  */
  state?: Content.Components['ProductModuleState'];
  /** The tag */
  tag?: Content.Components['ProductTagDto'];
  /** The variant */
  variant?: Content.Components['ProductModuleVariant'];
}

const BookingExtraTeaser = ({
  children,
  className,
  description,
  headline,
  images,
  isFetching = false,
  isInteractable = true,
  isInWishlist,
  isLoading = false,
  onClickButton,
  onClickEditLink,
  overlayDescription,
  overlayHeadline,
  priceItems,
  shouldHighlightErrors = false,
  state,
  tag,
  variant,
}: BookingExtraTeaserProps) => {
  const { t } = useI18n();
  const [showMoreModal, setShowMoreModal] = useState(false);
  const hasTag = tag && tag.tagText !== '';

  if (isLoading)
    return (
      <div
        className={classNames(styles.component, styles.isLoading, className)}
      >
        <div className={styles.mainWrapper}>
          <SkeletonLoader className={styles.loader} height="25px" width="35%" />

          <div className={styles.bottomSection}>
            <SkeletonLoader
              className={styles.loader}
              height="54px"
              width="100%"
            />

            <SkeletonLoader
              className={styles.loader}
              height="54px"
              width="100%"
            />
          </div>
        </div>
      </div>
    );

  return (
    <div className={classNames(styles.component, className)}>
      <div
        className={classNames(styles.mainWrapper, {
          [styles.hasTag]: hasTag,
        })}
      >
        {hasTag && (
          <Tag
            className={styles.tag}
            text={tag.tagText}
            variant={tag.tagType as TagVariant}
          />
        )}

        {variant === 'normal' && images && images.length > 0 && (
          <div className={styles.imageWrapper}>
            <ImageSlider
              ignoreMinimumSize
              images={images.map(img => ({
                altText: img.image.altText,
                url: img.image.url,
              }))}
            />
          </div>
        )}

        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            <div className={styles.headlineWrapper}>
              <Typography
                className={styles.headline}
                tag="h3"
                tagStyle="headingSmall"
              >
                {headline}
              </Typography>

              {shouldHighlightErrors && (
                <Typography
                  className={styles.error}
                  text={`*${t('items.modules.bookingFlow.required')}`}
                />
              )}
            </div>

            {variant === 'normal' && description && (
              <TruncatedText
                onShowMoreClick={() => setShowMoreModal(true)}
                richText={description}
                showMoreText={t('items.modules.bookingFlow.readMore')}
              />
            )}

            {(state === 'fullSelected' || state === 'partialSelected') &&
              children &&
              children}
          </div>

          <div className={styles.bottomSection}>
            {!isLoading && isFetching ? (
              <React.Fragment>
                <SkeletonLoader
                  className={styles.loader}
                  height="54px"
                  width="100%"
                />

                <SkeletonLoader
                  className={styles.loader}
                  height="54px"
                  width="100%"
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <PriceItems priceItems={priceItems ?? []} />

                {(state === 'fullSelected' || state === 'partialSelected') && (
                  <div className={styles.selectedActionWrapper}>
                    {isInteractable && (
                      <Link
                        className={styles.link}
                        onClick={() => onClickEditLink?.()}
                      >
                        <Typography tagStyle="bodyMedium">
                          {t('items.modules.bookingFlow.edit')}
                        </Typography>
                      </Link>
                    )}

                    <AlertPill
                      text={
                        isInWishlist
                          ? t('items.modules.myTripWishlist.inBasket')
                          : t('items.modules.bookingFlow.added')
                      }
                      variant="success"
                    />
                  </div>
                )}

                {state === 'unselected' && (
                  <div className={styles.unselectedActionWrapper}>
                    <Button
                      disabled={!isInteractable}
                      onClick={() => onClickButton?.()}
                      tabIndex={-1}
                      text={t('items.common.words.choose')}
                      variant="secondary"
                    />
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        </div>
      </div>

      <ExtraCardModal
        description={overlayDescription ?? description}
        headline={overlayHeadline ?? headline}
        images={images ?? []}
        isOpen={showMoreModal}
        onClose={() => setShowMoreModal(false)}
        priceItems={
          priceItems
            ? priceItems.map(item => ({
                label: item.label ?? null,
                price: item.price ?? null,
                tooltip: null,
              }))
            : []
        }
      />
    </div>
  );
};

BookingExtraTeaser.displayName = 'BookingExtraTeaser';

export default BookingExtraTeaser;
